'use client';

import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';

// Static version file path in the dist folder
const VERSION_FILE_URL = '/version.json';
const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const STORAGE_KEY = 'app_build_hash_dismissed';

// Current build hash - replaced by build process
const CURRENT_BUILD_HASH = '1551063cadff';

interface VersionData {
  buildHash: string;
  buildTime: string;
}

interface VersionCheckContextType {
  isOutdated: boolean;
  shouldShowPopup: boolean;
  dismissPopup: () => void;
  refreshApp: () => void;
}

const VersionCheckContext = createContext<VersionCheckContextType | undefined>(undefined);

export const useVersionCheck = () => {
  const context = useContext(VersionCheckContext);
  if (!context) {
    throw new Error('useVersionCheck must be used within a VersionCheckProvider');
  }
  return context;
};

export const VersionCheckProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOutdated, setIsOutdated] = useState(false);
  const [shouldShowPopup, setShouldShowPopup] = useState(false);
  const [currentBuildHash, setCurrentBuildHash] = useState(CURRENT_BUILD_HASH);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch(VERSION_FILE_URL, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });

      if (response.ok) {
        const data: VersionData = await response.json();
        const isBuildOutdated = data.buildHash !== currentBuildHash;

        setIsOutdated(isBuildOutdated);

        if (isBuildOutdated) {
          const dismissedBuildHash = localStorage.getItem(STORAGE_KEY);
          if (dismissedBuildHash !== data.buildHash) {
            setShouldShowPopup(true);
          }
        } else {
          // If versions match, ensure popup is hidden and clear any dismissed hash
          setShouldShowPopup(false);
        }
      }
    } catch (error) {
      console.error('Failed to check version:', error);
    }
  }, [currentBuildHash]);

  const dismissPopup = async () => {
    setShouldShowPopup(false);
    // Get the latest build hash to store
    try {
      const response = await fetch(VERSION_FILE_URL);
      if (response.ok) {
        const data: VersionData = await response.json();
        localStorage.setItem(STORAGE_KEY, data.buildHash);
      }
    } catch (error) {
      console.error('Failed to store dismissed hash:', error);
    }
  };

  const refreshApp = async () => {
    try {
      // Get the latest build hash before refreshing
      const response = await fetch(VERSION_FILE_URL, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });

      if (response.ok) {
        const data: VersionData = await response.json();
        // Update our current build hash to the latest one
        setCurrentBuildHash(data.buildHash);
        // Mark this version as seen to prevent popup after refresh
        localStorage.setItem(STORAGE_KEY, data.buildHash);
        // Clear the outdated state and popup
        setIsOutdated(false);
        setShouldShowPopup(false);
      }
    } catch (error) {
      console.error('Failed to update build hash before refresh:', error);
    }

    // Small delay to ensure state updates are processed
    setTimeout(() => {
      // Force hard refresh by navigating to a clean URL with cache-busting
      const baseUrl = window.location.origin + window.location.pathname;
      window.location.href = baseUrl + '?_=' + Date.now();
    }, 100);
  };

  useEffect(() => {
    checkVersion();
    intervalRef.current = setInterval(checkVersion, CHECK_INTERVAL);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  // Clear stale dismissal data on mount if versions now match
  useEffect(() => {
    const clearStaleData = async () => {
      try {
        const response = await fetch(VERSION_FILE_URL, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
          },
        });

        if (response.ok) {
          const data: VersionData = await response.json();
          // If current version matches server version, clear any dismissal
          if (data.buildHash === currentBuildHash) {
            const dismissedHash = localStorage.getItem(STORAGE_KEY);
            if (dismissedHash && dismissedHash !== data.buildHash) {
              localStorage.removeItem(STORAGE_KEY);
            }
          }
        }
      } catch (error) {
        console.error('Failed to clear stale dismissal data:', error);
      }
    };

    clearStaleData();
  }, [currentBuildHash]);

  const value = {
    isOutdated,
    shouldShowPopup,
    dismissPopup,
    refreshApp,
  };

  return <VersionCheckContext.Provider value={value}>{children}</VersionCheckContext.Provider>;
};
